import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import { X, Pause, Play, RotateCcw, Share2 } from "lucide-react";
import { ShareCard } from "@/v2/ShareCard";
import { api } from "@/lib/api";
import { Logo } from "@/components/AppShell";

const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
const up = (i = 0) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.25 + i * 0.35, duration: 0.8, ease: [0.2, 0.7, 0.2, 1] } });

function Count({ to, ms = 1600, decimals = 0 }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf, t0;
    const step = (t) => { t0 = t0 || t; const p = Math.min(1, (t - t0) / ms); setV(to * (1 - Math.pow(1 - p, 3))); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, ms]);
  return <>{v.toFixed(decimals)}</>;
}

const Eyebrow = ({ children, i = 0 }) => <motion.div {...up(i)} className="text-[11px] tracking-[0.22em] uppercase font-semibold text-white/50">{children}</motion.div>;
const Big = ({ children, i = 1, className = "" }) => <motion.h2 {...up(i)} className={`font-display tracking-tight leading-[1.05] text-4xl sm:text-6xl ${className}`}>{children}</motion.h2>;

function buildSlides(d) {
  const first = (d.name || "").split(" ")[0];
  const s = [];
  s.push({ id: "intro", bg: "#2D2926", img: "/images/beach-dawn.jpg", ms: 6500, el: (
    <>
      <Eyebrow>Year {d.year} · {d.final ? "Your year-end reveal" : `Your year so far · week ${d.week}`}</Eyebrow>
      <Big>{first}, this is the story of your year.</Big>
      {d.why && <motion.p {...up(2)} className="mt-8 text-lg text-white/70 italic font-display max-w-md">On {fmt(d.started_at)} you wrote: "{d.why}"</motion.p>}
    </>) });
  s.push({ id: "showing-up", bg: "#5A3E4E", ms: 7000, el: (
    <>
      <Eyebrow>Showing up</Eyebrow>
      <Big>You showed up on <span className="text-[#E6B057]"><Count to={d.days_showed_up} /></span> days.</Big>
      <motion.div {...up(2)} className="grid grid-cols-3 gap-4 mt-10 max-w-md">
        {[[d.steps_done, "steps done"], [d.challenges_done, "challenges"], [d.recovery_days, "recovery days, and they counted"]].map(([n, l]) => (
          <div key={l}><div className="font-display text-4xl"><Count to={n} /></div><div className="text-xs text-white/60 mt-1">{l}</div></div>
        ))}
      </motion.div>
    </>) });
  if (d.phases.length) s.push({ id: "phases", bg: "#1F2A24", ms: 7500, el: (
    <>
      <Eyebrow>Your chapters</Eyebrow>
      <Big>{d.phases.length === 1 ? "One chapter opened." : `${d.phases.length} chapters, at your own pace.`}</Big>
      <div className="mt-10 space-y-3 max-w-md">
        {d.phases.map((p, i) => (
          <motion.div key={p.key} {...up(2 + i * 0.6)} className="flex items-center gap-4">
            <span className="h-3 w-3 rounded-full" style={{ background: p.color }} />
            <span className="font-display text-2xl w-36"><span style={{ color: p.color }}>{p.letter}</span> {p.name}</span>
            <motion.span initial={{ width: 0 }} animate={{ width: `${Math.min(100, p.weeks * 7)}%` }} transition={{ delay: 0.9 + i * 0.25, duration: 1 }} className="h-1.5 rounded-full max-w-[9rem]" style={{ background: p.color }} />
            <span className="text-sm text-white/60">{p.weeks} wk{p.state === "current" ? " · now" : ""}</span>
          </motion.div>
        ))}
      </div>
    </>) });
  s.push({ id: "pillars", bg: "#3A2F2A", ms: 8000, el: (
    <>
      <Eyebrow>Then vs now</Eyebrow>
      <Big>How you rate your life.</Big>
      <div className="mt-10 space-y-5 max-w-md">
        {d.pillars.map((p, i) => (
          <motion.div key={p.key} {...up(2 + i * 0.4)}>
            <div className="flex justify-between text-sm"><span className="font-semibold">{p.label}</span><span className="text-white/60">{p.then ?? "–"} → <b className="text-white text-base">{p.now ?? p.then ?? "–"}</b></span></div>
            <div className="relative h-2 rounded-full bg-white/10 mt-2">
              <div className="absolute inset-y-0 left-0 rounded-full bg-white/25" style={{ width: `${(p.then || 0) * 10}%` }} />
              <motion.div initial={{ width: `${(p.then || 0) * 10}%` }} animate={{ width: `${(p.now ?? p.then ?? 0) * 10}%` }} transition={{ delay: 1.2 + i * 0.4, duration: 1.2 }} className="absolute inset-y-0 left-0 rounded-full bg-[#E6B057]" />
            </div>
          </motion.div>
        ))}
      </div>
    </>) });
  if (d.weight && d.weight.lost > 0) {
    const k = d.weight.unit === "lb" ? 2.20462 : 1;
    s.push({ id: "weight", bg: "#7A3B2A", ms: 6500, el: (
      <>
        <Eyebrow>Weight Reset</Eyebrow>
        <Big><span className="text-[#F3C98B]"><Count to={d.weight.lost * k} ms={1800} decimals={1} /></span><span className="text-3xl sm:text-5xl"> {d.weight.unit} lighter</span></Big>
        <motion.p {...up(2)} className="mt-6 text-xl font-display">On your trend line, not a lucky weigh-in. {d.weight.progress_pct}% of the way to your goal.</motion.p>
        <motion.p {...up(3)} className="mt-3 text-sm text-white/60 max-w-sm">Steady and safe. And it's only one of five parts of your life that changed.</motion.p>
      </>) });
  }
  if (d.wins.length) s.push({ id: "wins", bg: "#2D2926", ms: Math.min(12000, 5000 + d.wins.length * 600), el: (
    <>
      <Eyebrow>Evidence</Eyebrow>
      <Big><Count to={d.wins.length} /> real wins.</Big>
      <div className="mt-8 space-y-2.5 max-w-md max-h-[45vh] overflow-hidden">
        {d.wins.slice(-10).map((w, i) => (
          <motion.div key={w.id} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 + i * 0.45, duration: 0.6 }} className="flex gap-3 items-baseline">
            <span className="text-[11px] text-white/40 w-14 shrink-0">{new Date(w.date + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
            <span className="text-[15px]">{w.text}</span>
          </motion.div>
        ))}
      </div>
    </>) });
  if (d.top_categories.length || d.motivations.length) s.push({ id: "moves-you", bg: "#2E3B47", ms: 7000, el: (
    <>
      <Eyebrow>What moves you</Eyebrow>
      <Big>CAMRYN learned a few things about you.</Big>
      <div className="mt-8 space-y-4 max-w-md">
        {d.top_categories.length > 0 && <motion.p {...up(2)} className="text-lg">You keep coming back to <b>{d.top_categories.join(", ")}</b>.</motion.p>}
        {d.motivations.map((m, i) => <motion.p key={m} {...up(3 + i * 0.5)} className="text-lg font-display italic text-white/80">{m}.</motion.p>)}
      </div>
    </>) });
  if (d.letters.length) s.push({ id: "letters", bg: "#4A3A33", ms: 10000, el: (
    <>
      <Eyebrow>From your chapter letters</Eyebrow>
      <div className="mt-6 space-y-6 max-w-lg">
        {d.letters.slice(-2).map((l, i) => (
          <motion.blockquote key={l.id} {...up(1 + i)} className="font-display text-xl leading-relaxed text-white/90 border-l-2 border-[#E6B057] pl-5">
            "{l.text.split(". ").slice(0, 2).join(". ")}."<span className="block text-xs text-white/50 mt-2 font-sans not-italic">End of {l.phase_name}</span>
          </motion.blockquote>
        ))}
      </div>
    </>) });
  s.push({ id: "letter", bg: "#FBF9F5", light: true, ms: 0, el: (
    <>
      <motion.div {...up(0)} className="text-[11px] tracking-[0.22em] uppercase font-semibold text-stone-400">A letter for you</motion.div>
      <motion.p {...up(1)} className="font-display text-lg sm:text-xl leading-relaxed text-stone-800 mt-6 whitespace-pre-line max-w-xl" data-testid="recap-year-letter">{d.letter}</motion.p>
    </>) });
  return s;
}

export default function V2Recap() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [i, setI] = useState(0);
  const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const [paused, setPaused] = useState(!!reduce);
  const [sharing, setSharing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const slides = useMemo(() => (d ? buildSlides(d) : []), [d]);
  const cur = slides[i];
  const last = i === slides.length - 1;
  const raf = useRef();

  useEffect(() => { api.get("/v2/recap").then(({ data }) => setD(data)).catch(() => nav("/journey")); }, [nav]);
  const go = useCallback((n) => { setI((x) => Math.max(0, Math.min(slides.length - 1, x + n))); setElapsed(0); }, [slides.length]);

  useEffect(() => {
    if (!cur || paused || !cur.ms) return;
    let prev;
    const tick = (t) => {
      if (prev) setElapsed((e) => { const n = e + (t - prev); if (n >= cur.ms) { go(1); return 0; } return n; });
      prev = t;
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [cur, paused, go]);

  useEffect(() => {
    const k = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); if (e.key === " ") { e.preventDefault(); setPaused((p) => !p); } if (e.key === "Escape") nav("/journey"); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [go, nav]);

  if (!d) return (
    <div className="fixed inset-0 bg-[#2D2926] text-white grid place-items-center" data-testid="recap-loading">
      <div className="text-center"><Logo className="text-white" /><p className="font-display text-xl mt-6 animate-pulse">Gathering your year…</p></div>
    </div>
  );

  const nextYear = async () => { await api.post("/v2/journey/next-year"); nav("/journey"); };
  return (
    <MotionConfig reducedMotion="user">
    <ShareCard d={d} open={sharing} onOpenChange={setSharing} />
    <motion.div className="fixed inset-0 overflow-hidden select-none" animate={{ backgroundColor: cur.bg }} transition={{ duration: 0.9 }} data-testid="recap">
      <AnimatePresence>
        {cur.img && <motion.img key={cur.img} src={cur.img} alt="" aria-hidden="true" data-testid="recap-intro-image" initial={{ opacity: 0 }} animate={{ opacity: 0.35 }} exit={{ opacity: 0 }} transition={{ duration: 0.9 }} className="absolute inset-0 h-full w-full object-cover pointer-events-none" />}
      </AnimatePresence>
      <div className="absolute top-0 inset-x-0 z-30 px-4 pt-4 flex gap-1.5" data-testid="recap-progress">
        {slides.map((s, k) => (
          <div key={s.id} className={`h-[3px] flex-1 rounded-full overflow-hidden ${cur.light ? "bg-stone-900/15" : "bg-white/20"}`}>
            <div className={`h-full ${cur.light ? "bg-stone-900" : "bg-white"}`} style={{ width: k < i ? "100%" : k === i ? (s.ms ? `${(elapsed / s.ms) * 100}%` : "100%") : "0%" }} />
          </div>
        ))}
      </div>
      <div className={`absolute top-0 pt-8 pb-3 inset-x-0 z-20 px-5 flex items-center justify-between ${cur.light ? "text-stone-800 bg-[#FBF9F5]/95" : "text-white"}`}>
        <Logo className="!text-base" />
        <div className="flex gap-3">
          {cur.ms > 0 && <button onClick={() => setPaused(!paused)} data-testid="recap-pause-btn" aria-label={paused ? "Play" : "Pause"}>{paused ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}</button>}
          <button onClick={() => nav("/journey")} data-testid="recap-close-btn" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
      </div>
      <button className="absolute left-0 top-20 bottom-0 w-1/3 z-10" onClick={() => go(-1)} data-testid="recap-prev" aria-label="Previous" />
      {!last && <button className="absolute right-0 top-20 bottom-0 w-2/3 z-10" onClick={() => go(1)} data-testid="recap-next" aria-label="Next" />}
      <AnimatePresence initial={false}>
        <motion.section key={cur.id} data-testid={`recap-slide-${cur.id}`} initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98, transition: { duration: 0.25 } }} transition={{ duration: 0.5 }}
          className={`absolute inset-0 max-w-3xl mx-auto px-7 sm:px-12 flex flex-col ${last ? "z-[15]" : ""} ${cur.light ? "justify-start text-stone-900 overflow-y-auto pt-28 pb-12" : "justify-center text-white"}`}>
          {cur.el}
          {last && (
            <div className="relative z-20 mt-8 flex flex-wrap gap-3">
              {d.final ? <button onClick={nextYear} data-testid="recap-next-year-btn" className="btn-ink px-6 h-12 rounded-full font-semibold">Begin year {d.year + 1}</button>
                : <button onClick={() => nav("/today")} data-testid="recap-keep-going-btn" className="btn-ink px-6 h-12 rounded-full font-semibold">Keep going: week {d.week + 1} awaits</button>}
              <button onClick={() => setSharing(true)} data-testid="recap-share-btn" className="px-5 h-12 rounded-full border border-stone-300 text-sm font-semibold inline-flex items-center gap-2"><Share2 className="h-4 w-4" />Share my year</button>
              <button onClick={() => { setI(0); setElapsed(0); }} data-testid="recap-replay-btn" className="px-5 h-12 rounded-full border border-stone-300 text-sm font-semibold inline-flex items-center gap-2"><RotateCcw className="h-4 w-4" />Replay</button>
            </div>
          )}
        </motion.section>
      </AnimatePresence>
    </motion.div>
    </MotionConfig>
  );
}
